import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { ManageMetadataEditDrawer } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataEditDrawer';
import {
  useFetchCoverageForMultipleMetadataFields,
  useFetchMetadataDetailsForEqlSource,
  useGetManageMetadataTableColumns,
} from '@modules/integrationsV2/hooks';
import { useManageMetadataEditDrawerPagination } from '@modules/integrationsV2/hooks/useManageMetadataEditDrawerPagination';
import {
  IntegrationSource,
  ManageMetadataTableRow,
} from '@modules/integrationsV2/types';
import {
  mapIntegrationTypeToObjectType,
  trackIntegrationPageManageMetadataEvent,
} from '@modules/integrationsV2/utils';
import { CreateUnifiedFieldDrawer } from '@modules/ManageUnifiedFields/components/CreateUnifiedFieldDrawer';
import { EditUnifiedFieldDrawer } from '@modules/ManageUnifiedFields/components/EditUnifiedFieldDrawer';
import { SearchRounded } from '@mui/icons-material';
import { InputAdornment, Stack, TextField } from '@mui/material';
import {
  DataGridPro,
  gridFilteredSortedRowIdsSelector,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  IntegrationType,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 8;
interface ManageMetadataTableProps {
  integrationSource: IntegrationSource;
}

export const ManageMetadataTable = ({
  integrationSource,
}: ManageMetadataTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const canEditMetadataFields = useIsAuthorized({
    operation: PermissionOperation.Write,
    resource: PermissionResource.Metadata,
  });
  const {
    metadataDetails,
    isLoading,
    refetch: refetchMetadataTable,
  } = useFetchMetadataDetailsForEqlSource({
    eqlSource: integrationSource.source ?? '',
    objectType: mapIntegrationTypeToObjectType(
      integrationSource.integrationType,
    ),
  });

  const apiRef = useGridApiRef();

  const filteredAndSortedMetadataDetails = useMemo(() => {
    if (!searchQuery) {
      return metadataDetails;
    }

    const filteredRows = metadataDetails.filter(
      (metadata) =>
        metadata.displayText
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        metadata.originalDisplayText
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
    );

    // Higher priority for 'displayText' match over 'originalDisplayText'
    return orderBy(filteredRows, (metadata) =>
      metadata.displayText.toLowerCase().includes(searchQuery.toLowerCase())
        ? 0
        : 1,
    );
  }, [metadataDetails, searchQuery]);

  const [visibleRows, setVisibleRows] = useState<ManageMetadataTableRow[]>([]);

  useEffect(() => {
    if (!apiRef.current) {
      setVisibleRows([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      const sortedRows =
        apiRef.current.getSortedRows() as ManageMetadataTableRow[];

      const start = paginationModel.page * paginationModel.pageSize;
      const end = start + paginationModel.pageSize;

      setVisibleRows(sortedRows.slice(start, end));
    }, 0); // Workaround for "getSortedRows is not a function" error, ref: https://github.com/mui/mui-x/issues/6411#issuecomment-1271556519

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, paginationModel.page, paginationModel.pageSize, sortModel]);

  const { valuesMap } = useFetchCoverageForMultipleMetadataFields({
    metadataFields: visibleRows,
    metadataIntegrationType: integrationSource.integrationType,
    shouldSkip: integrationSource.integrationType !== IntegrationType.Feedback,
  });

  const mappedTableRows = useMemo((): ManageMetadataTableRow[] => {
    return filteredAndSortedMetadataDetails.map((metadata) => ({
      ...metadata,
      coverage: valuesMap[metadata.key],
    }));
  }, [filteredAndSortedMetadataDetails, valuesMap]);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isEditUnifiedFieldDrawerOpen, setIsEditUnifiedFieldDrawerOpen] =
    useState(false);

  const [
    shouldOpenCreateUnifiedFieldDrawer,
    setShouldOpenCreateUnifiedFieldDrawer,
  ] = useState(false);

  const [selectedUnifiedFieldKeyForEdit, setSelectedUnifiedFieldKeyForEdit] =
    useState<string | null>(null);

  const [
    selectedMetadataKeyForUnifiedField,
    setSelectedMetadataKeyForUnifiedField,
  ] = useState<string | null>(null);

  const [
    shouldUpdateMetadataUnifiedFields,
    setShouldUpdateMetadataUnifiedFields,
  ] = useState<boolean>(false);

  const {
    onMetadataToEditChange,
    onCancel,
    onRowsChange,
    onNext,
    onPrevious,
    metadataToEdit,
    currentFieldIndex,
  } = useManageMetadataEditDrawerPagination({
    metadataData: mappedTableRows,
  });

  const onEditButtonClick = (metadata: ManageMetadataTableRow) => {
    setIsEditDrawerOpen(true);
    onMetadataToEditChange(metadata);
    trackIntegrationPageManageMetadataEvent({
      eventName: 'Manage Metadata Table - Edit Metadata Button Clicked',
      integration: {
        channelType: integrationSource.channel.type,
        integrationType: integrationSource.integrationType,
        namespace: integrationSource.namespace,
      },
    });
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    onCancel();
  };

  const openEditUnifiedFieldDrawer = (metadataKey?: string) => {
    if (!metadataKey) {
      return;
    }

    setIsEditUnifiedFieldDrawerOpen(true);
    setSelectedUnifiedFieldKeyForEdit(metadataKey);
  };

  const openCreateUnifiedFieldDrawer = (metadataKey?: string) => {
    if (!metadataKey) {
      return;
    }
    setSelectedMetadataKeyForUnifiedField(metadataKey ?? '');
    setShouldOpenCreateUnifiedFieldDrawer(true);
  };

  const handleUnifiedFieldUpdates = () => {
    refetchMetadataTable();
    setShouldUpdateMetadataUnifiedFields(true);
  };

  const columns = useGetManageMetadataTableColumns({
    onEditButtonClick,
    onEditUnifiedFieldButtonClick: openEditUnifiedFieldDrawer,
    openCreateUnifiedFieldDrawer: openCreateUnifiedFieldDrawer,
    integrationSource,
    shouldUpdateMetadataUnifiedFields: shouldUpdateMetadataUnifiedFields,
  });

  const getNoRowsOverlayText = () => {
    if (searchQuery) {
      return `No matches found for "${searchQuery}"`;
    }

    return `No metadata fields found for ${
      integrationSource.channel.displayName
    } ${
      integrationSource.namespace ? ` - ${integrationSource.namespace}` : ''
    }`;
  };

  const shouldHideFooter = mappedTableRows.length === 0;
  return (
    <Stack>
      <TextField
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          trackIntegrationPageManageMetadataEvent({
            eventName: 'Manage Metadata Table - Search Operation Performed',
            integration: {
              channelType: integrationSource.channel.type,
              integrationType: integrationSource.integrationType,
              namespace: integrationSource.namespace,
            },
          });
        }}
        placeholder="Search"
        fullWidth
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded
                sx={{
                  color: 'text.secondary',
                }}
              />
            </InputAdornment>
          ),
        }}
      />
      <DataGridPro
        apiRef={apiRef}
        sx={{
          border: 'none',
          '& .MuiTablePagination-displayedRows': {
            marginTop: '1rem', // to align pagination text
          },
          '& .MuiDataGrid-row': {
            position: 'relative',
            '&: hover': {
              cursor: 'pointer',
              '.js-manage-metadata-row-action-button-group': {
                opacity: 1,
              },
            },
          },
        }}
        loading={isLoading}
        columns={columns}
        rows={mappedTableRows}
        pagination
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } },
        }}
        hideFooter={shouldHideFooter}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[PAGE_SIZE]}
        getRowId={({ key }) => key}
        autoHeight
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 40}
        columnHeaderHeight={45}
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={(params) => {
          if (canEditMetadataFields) {
            onEditButtonClick(params.row);
          }
        }}
        slots={{
          noRowsOverlay: () =>
            !isLoading ? (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {getNoRowsOverlayText()}
              </Stack>
            ) : null,
        }}
        onStateChange={(state) => {
          const rowIDs = gridFilteredSortedRowIdsSelector(state).map((id) =>
            id.toString(),
          );
          onRowsChange(rowIDs);
        }}
      />
      <ManageMetadataEditDrawer
        metadataToEdit={metadataToEdit}
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        onNext={onNext}
        onPrevious={onPrevious}
        currentFieldIndex={currentFieldIndex}
        totalFieldCount={metadataDetails.length}
        integrationSource={integrationSource}
        onEditUnifiedFieldButtonClick={openEditUnifiedFieldDrawer}
        openCreateUnifiedFieldDrawer={openCreateUnifiedFieldDrawer}
      />
      {selectedUnifiedFieldKeyForEdit && (
        <EditUnifiedFieldDrawer
          open={isEditUnifiedFieldDrawerOpen}
          onClose={() => {
            setIsEditUnifiedFieldDrawerOpen(false);
            setSelectedUnifiedFieldKeyForEdit(null);
          }}
          metadataKey={selectedUnifiedFieldKeyForEdit ?? ''}
          onSubmit={handleUnifiedFieldUpdates}
        />
      )}

      <CreateUnifiedFieldDrawer
        open={shouldOpenCreateUnifiedFieldDrawer}
        onClose={() => {
          setShouldOpenCreateUnifiedFieldDrawer(false);
          setSelectedMetadataKeyForUnifiedField(null);
        }}
        metadataKey={selectedMetadataKeyForUnifiedField ?? ''}
        onSubmit={handleUnifiedFieldUpdates}
      />
    </Stack>
  );
};
